import React from "react";
import UsersListTable from "../components/User/UsersListTable";
import { Box } from "braid-design-system";
import Loading from "../components/Loading";
import { getAllUsers, UserWithActiveStatusResponse } from "../services/userService";
import { useToast } from "../hooks/useToast";
import CreateUpdateUserDrawer from "../components/User/CreateUpdateUserDrawer";
import { useCanManageMasters } from "../hooks/useCanManageMasters";

const UserManagement: React.FC = () => {
    const canManageMasters = useCanManageMasters();
    const [loading, setLoading] = React.useState(false);
    const [loadingMessage, setLoadingMessage] = React.useState<string | null>(null);
    const [usersList, setUsersList] = React.useState<Array<UserWithActiveStatusResponse> | null>(null);
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const [selectedUser, setSelectedUser] = React.useState<UserWithActiveStatusResponse | undefined>(undefined);
    const toast = useToast()

    const fetchUsers = React.useCallback(() => {
        setLoading(true);
        setLoadingMessage("Please wait, Fetching users...");
        getAllUsers().then((users) => {
            setUsersList(users);
        }).catch((error) => {
            // Handle error if needed
            toast.showError(`Error fetching users: ${error?.message || 'Unknown error'}`);
        }).finally(() => {
            setLoading(false);
            setLoadingMessage(null);
        });
    }, [toast]);

    const handleUserCreated = React.useCallback(() => {
        // Refresh the user list after a new user is created
        fetchUsers();
    }, [fetchUsers]);

    const handleEditUser = React.useCallback((userId: number) => {
        // Find the user to edit
        const user = usersList?.find(u => u.id === userId);
        setSelectedUser(user);
        setDrawerOpen(true);
    }, [usersList]);

    const handleCloseDrawer = React.useCallback(() => {
        setDrawerOpen(false);
        setSelectedUser(undefined);
    }, []);

    React.useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    return (
        <>
            {
                loading && <Loading message={loadingMessage || "Loading..."} />
            }
            <Box padding={"medium"}>
                <UsersListTable
                    userList={usersList}
                    onEditUser={canManageMasters ? handleEditUser : undefined}
                    onCreateUser={canManageMasters ? () => { setDrawerOpen(true); } : undefined}
                    onUserUpdated={fetchUsers}
                />
            </Box>

            <CreateUpdateUserDrawer
                isOpen={drawerOpen}
                onClose={handleCloseDrawer}
                userToEdit={selectedUser}
                onUserCreated={handleUserCreated}
            />
        </>
    );
}

export default UserManagement;